import toast from 'react-hot-toast'
import GenerateQuoteData from '../common/GenerateQuoteData'

export default class QuoteData {
   GeneratedQuoteData = null
   AuthStatus = 400
   host_url = null

   /*****************************************/
   /* Custom Functions for the Client Side */
   /*          Update Server Side          */
   /*****************************************/

   async getTimeData() {
      const response = await fetch(`${this.host_url}/api/time`)
      if (response.ok) {
         const data = await response.json()
         if (Object.keys(data).length === 0) {
            toast.error(`Error Retrieving Server Time`, { duration: 5000 })
            return null
         }
         return data
      } else {
         toast.error(
            'Error Retrieving Server Time\n' + (await response.text()),
            { duration: 5000 },
         )
         return null
      }
   }

   async getOppData(opportunity_id) {
      const response = await fetch(
         `${this.host_url}/api/get-opp?opportunity_id=${opportunity_id}`,
      )
      if (response.ok) {
         const parsed = await response.json()
         if (Object.keys(parsed).length === 0) {
            toast.error(
               <div style={{ wordBreak: 'keep-all' }}>
                  <b>Please save some products and click refresh.</b> <br />
                  If this issue persists, please file a ticket at
                  <b> datadesk.bamboohr.net</b> <br />
                  <br />
                  Unable to find opportunity {opportunity_id} in the database.
                  This could be because no products have been saved.
               </div>,
               { duration: 10000 },
            )
            return null
         }
         return parsed
      } else {
         toast.error(
            'Error Retrieving Opportunity Data\n' + (await response.text()),
            { duration: 5000 },
         )
         return null
      }
   }

   async getCurrencyObject(currencyCode) {
      let response = await fetch(
         `${this.host_url}/api/currency?currencyCode=${currencyCode}`,
      )
      if (response.ok) {
         const data = await response.json()
         if (Object.keys(data).length === 0) {
            toast.error(`Error Retrieving Currency Data`, { duration: 5000 })
            return null
         }
         return data
      } else {
         toast.error(
            'Error Retrieving Currency Data\n' + (await response.text()),
            { duration: 5000 },
         )
         return null
      }
   }

   async getProductList(pricebookId, currencyObject) {
      const response = await fetch(
         `${this.host_url}/api/products?book_id=${pricebookId}&currencyCode=${currencyObject.Code}`,
      )
      if (response.ok) {
         const data = await response.json()
         if (Object.keys(data).length === 0) {
            toast.error(
               `Error Retrieving Product List for pricebook ${pricebookId}`,
               { duration: 5000 },
            )
            return null
         }
         return data
      } else {
         toast.error(
            'Error Retrieving Product List\n' + (await response.text()),
            { duration: 5000 },
         )
         return null
      }
   }

   async getDiscountList(
      pricebookId,
      PromoUTM,
      AccountFlags,
      currencyObject,
      OpportunityType,
      accountSnowflake,
      partnerAccount,
      region,
      opportunity_id,
   ) {
      const response = await fetch(
         `${this.host_url}/api/discounts?book_id=${pricebookId}&promo_utm=${encodeURIComponent(PromoUTM)}&account_flags=${AccountFlags}&currencyCode=${currencyObject.Code}&opportunity_type=${OpportunityType}&account_snowflake=${encodeURIComponent(JSON.stringify(accountSnowflake))}&partner_account=${partnerAccount}&region=${encodeURIComponent(region || '')}&opportunity_id=${opportunity_id}`,
      )
      if (response.ok) {
         const data = await response.json()
         if (Object.keys(data).length === 0) {
            toast.error(
               `Error Retrieving Discount List for pricebook ${this.pricebookId}`,
               { duration: 5000 },
            )
            return null
         }
         return data
      } else {
         toast.error(
            'Error Retrieving Discount List\n' + (await response.text()),
            { duration: 5000 },
         )
         return null
      }
   }

   async getFeaturePermissions() {
      const response = await fetch(`${this.host_url}/api/feature-permissions`)
      if (response.ok) {
         let data = await response.json()

         if (Object.keys(data).length === 0) {
            toast.error(`Error Retrieving Feature Permissions`, {
               duration: 5000,
            })
            return null
         }
         return data
      }
   }

   /**
    * Fetches localization strings from the server
    * @returns {Promise<Object>} Map of LanguageCode to a Map of StringCode to String
    */
   async getStrings() {
      const response = await fetch(`${this.host_url}/api/strings`)
      if (response.ok) {
         const data = await response.json()
         if (Object.keys(data).length === 0) {
            toast.error('No localization strings found.', { duration: 5000 })
            return {}
         }
         // Convert the outer object to a Map and each inner object to a Map as well
         return new Map(
            Object.entries(data).map(([langCode, stringObj]) => [
               langCode,
               new Map(Object.entries(stringObj)),
            ]),
         )
      } else {
         toast.error(
            'Error Retrieving Localization Strings\n' + (await response.text()),
            { duration: 5000 },
         )
         return {}
      }
   }

   async getPricebooks(opportunity_id) {
      const response = await fetch(
         `${this.host_url}/api/pricebooks?opportunity_id=${opportunity_id}`,
      )
      if (response.ok) {
         const data = await response.json()
         if (Object.keys(data).length === 0) {
            toast.error(`Error Retrieving Pricebooks List`, { duration: 5000 })
            return null
         }
         return data
      } else {
         toast.error(
            'Error Retrieving Pricebook List\n' + (await response.text()),
            { duration: 5000 },
         )
         return null
      }
   }

   async getOpportunityProducts(opportunity_id) {
      const response = await fetch(
         `${this.host_url}/api/opportunity-products?opportunity_id=${opportunity_id}`,
      )
      if (response.ok) {
         const data = await response.json()
         const products = new Map(data || new Map())
         return products
      } else {
         toast.error(
            'Error Retrieving Opportunity Products\n' + (await response.text()),
            { duration: 5000 },
         )
         return new Map()
      }
   }

   async getTermLengths() {
      const response = await fetch(
         `${this.host_url}/api/term_lengths?opportunity_id=${this.opportunity_id ?? ''}`,
      )
      if (response.ok) {
         const data = await response.json()
         if (Object.keys(data).length === 0) {
            toast.error(`Error Retrieving Term Lengths List`, {
               duration: 5000,
            })
            return null
         }
         return data
      } else {
         toast.error(
            'Error Retrieving Term Lengths List\n' + (await response.text()),
            { duration: 5000 },
         )
         return null
      }
   }

   constructor(
      opportunity_id,
      user_role,
      hasPEPMCols = false,
      prefix = '',
      hasDiscountPrepaymentCol = false,
   ) {
      this.getTimeData = this.getTimeData.bind(this)
      this.getOppData = this.getOppData.bind(this)
      this.getCurrencyObject = this.getCurrencyObject.bind(this)
      this.getProductList = this.getProductList.bind(this)
      this.getDiscountList = this.getDiscountList.bind(this)
      this.getFeaturePermissions = this.getFeaturePermissions.bind(this)
      this.getPricebooks = this.getPricebooks.bind(this)
      this.getStrings = this.getStrings.bind(this)
      this.getOpportunityProducts = this.getOpportunityProducts.bind(this)
      this.getTermLengths = this.getTermLengths.bind(this)

      this.host_url = process.env.REACT_APP_HOST_URL
      this.opportunity_id = opportunity_id

      this.GeneratedQuoteData = new GenerateQuoteData(
         opportunity_id,
         user_role,
         hasPEPMCols,
         prefix,
         hasDiscountPrepaymentCol,
      )
   }

   /**
    * Calls QuoteData with all the custom functions for the client side.
    * This version of the function also checks for the user's authorization status.
    *
    * @returns either true or false depending on if the QuoteData was initialized successfully
    */
   async initializeQuoteData() {
      await fetch(`${this.host_url}/api/authorized`)
         .then((res) => {
            if (!res.ok) {
               throw new Error('Not authorized')
            }
         })
         .catch((err) => {
            // handle error
            console.error(err)
         })

      const result = await this.GeneratedQuoteData.initialize(
         this.getTimeData,
         this.getFeaturePermissions,
         this.getOppData,
         this.getProductList,
         this.getDiscountList,
         this.getCurrencyObject,
         this.getPricebooks,
         this.getOpportunityProducts,
         this.getStrings,
         this.getTermLengths,
      )
      delete this.GeneratedQuoteData['AllPermissions']
      return result
   }
}
